import { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSidebarStore, useUserRoles, usePowerBIStore, useThemeStore } from '@/store';
import { UserRole } from '@/permissions/types';
import { Modal, Button } from '@/components/ui';
import { getVersionString } from '@/config/version';

/**
 * Navigation item interface supporting nested menu items
 * - path: Optional route path (required for leaf items, optional for parent items with children)
 * - children: Optional array of nested NavItems for dropdown menus
 * - roles: Optional array of UserRole enums for permission-based visibility
 *
 * Example:
 * {
 *   name: 'Projects',
 *   icon: <ProjectIcon />,
 *   roles: [UserRole.FIXTURES],
 *   children: [
 *     { name: 'All Projects', path: '/projects', icon: <AllIcon /> },
 *     { name: 'Active', path: '/projects/active', icon: <ActiveIcon /> }
 *   ]
 * }
 */
interface NavItem {
  name: string;
  path?: string;
  icon: JSX.Element;
  children?: NavItem[];
  roles?: UserRole[];
}

const navItems: NavItem[] = [
  {
    name: 'Dashboard',
    path: '/',
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
        />
      </svg>
    ),
    // No roles required - dashboard is accessible to all authenticated users
  },
  {
    name: 'Pool Financial',
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
        />
      </svg>
    ),
    children: [
      {
        name: 'Fixed Days',
        path: '/fixed-days',
        roles: [UserRole.FIXED_DAYS],
        icon: (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        ),
      },
      {
        name: 'Annual Reports',
        path: '/reports/yearly/summary',
        roles: [UserRole.ANNUAL_REPORTS],
        icon: (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        ),
      },
      {
        name: 'Monthly Reports',
        path: '/reports/monthly/summary',
        roles: [UserRole.MONTHLY_REPORTS],
        icon: (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        ),
      },
      {
        name: 'Distribution Schedule',
        path: '/reports/distribution-schedule',
        roles: [UserRole.REPORTS_FILES],
        icon: (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
            />
          </svg>
        ),
      },
      {
        name: 'Pool Point Calculation',
        path: '/reports/pool-point-calculation',
        roles: [UserRole.REPORTS_FILES],
        icon: (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
            />
          </svg>
        ),
      },
    ],
  },
  {
    name: 'Market Information',
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
        />
      </svg>
    ),
    children: [
      {
        name: 'Daily Report',
        path: '/reports/daily',
        roles: [UserRole.DAILY_REPORT],
        icon: (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
            />
          </svg>
        ),
      },
      {
        name: 'Freight Market',
        icon: (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
            />
          </svg>
        ),
        children: [
          {
            name: 'Recent Fixtures',
            path: '/recent-fixtures',
            roles: [UserRole.RECENT_FIXTURES],
            icon: (
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            ),
          },
          {
            name: 'Cargo Count',
            path: '/cargo-count',
            roles: [UserRole.CARGO_COUNT],
            icon: (
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
            ),
          },
        ],
      },
      {
        name: 'Reports',
        roles: [
          UserRole.POWERBI_BASIC,
          UserRole.POWERBI_PRIVILEGED,
          UserRole.POWERBI_PRIVILEGED_KPC,
        ],
        icon: (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
        ),
        children: [], // Will be populated dynamically
      },
      {
        name: 'Baltic',
        path: '/baltic',
        roles: [UserRole.BALTIC],
        icon: (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        ),
      },
      {
        name: 'Bunker Prices',
        path: '/bunker-prices',
        roles: [UserRole.BUNKER_PRICES],
        icon: (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
            />
          </svg>
        ),
      },
    ],
  },
  {
    name: 'Chartering',
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
        />
      </svg>
    ),
    children: [
      {
        name: 'Fixtures',
        path: '/fixtures',
        roles: [UserRole.FIXTURES],
        icon: (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        ),
      },
      {
        name: 'Position List',
        path: '/position-list',
        roles: [UserRole.POSITION_LIST],
        icon: (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
        ),
      },
      {
        name: 'Cargo List',
        path: '/cargo-list',
        roles: [UserRole.CARGO_LIST],
        icon: (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
            />
          </svg>
        ),
      },
    ],
  },
  {
    name: 'Reports',
    path: '/reports/files',
    roles: [UserRole.REPORTS_FILES],
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    ),
  },
  {
    name: 'Administration',
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
      </svg>
    ),
    children: [
      {
        name: 'Users',
        path: '/users',
        roles: [UserRole.USERS],
        icon: (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
        ),
      },
      {
        name: 'Companies',
        path: '/companies',
        roles: [UserRole.COMPANIES],
        icon: (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
        ),
      },
      {
        name: 'User Groups',
        path: '/user-groups',
        roles: [UserRole.USER_GROUPS],
        icon: (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
        ),
      },
    ],
  },
  {
    name: 'Profile',
    path: '/user-profile',
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
];

const MENU_KEY_SEPARATOR = '__';

const normalizePath = (path: string): string => {
  const normalizedPath = path.replace(/\/+$/, '');
  return normalizedPath === '' ? '/' : normalizedPath;
};

const isPathActive = (pathname: string, itemPath?: string): boolean => {
  if (!itemPath) return false;

  const currentPath = normalizePath(pathname);
  const targetPath = normalizePath(itemPath);

  if (targetPath === '/') {
    return currentPath === '/';
  }

  return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
};

const getMenuItemKey = (item: NavItem, parentMenuKey = ''): string => {
  const keyPart = item.path ? `path:${item.path}` : `name:${item.name.toLowerCase()}`;
  return parentMenuKey ? `${parentMenuKey}${MENU_KEY_SEPARATOR}${keyPart}` : keyPart;
};

const isMenuKeyInOpenBranch = (menuKey: string, openMenuKey: string | null): boolean => {
  if (!openMenuKey) return false;
  return openMenuKey === menuKey || openMenuKey.startsWith(`${menuKey}${MENU_KEY_SEPARATOR}`);
};

const hasActiveDescendant = (item: NavItem, pathname: string): boolean => {
  if (isPathActive(pathname, item.path)) {
    return true;
  }

  if (!item.children || item.children.length === 0) {
    return false;
  }

  return item.children.some((child) => hasActiveDescendant(child, pathname));
};

const findDeepestActiveParentKey = (
  items: NavItem[],
  pathname: string,
  parentMenuKey = ''
): string | null => {
  for (const item of items) {
    const menuKey = getMenuItemKey(item, parentMenuKey);

    if (!item.children || item.children.length === 0) {
      continue;
    }

    const activeChildKey = findDeepestActiveParentKey(item.children, pathname, menuKey);
    if (activeChildKey) {
      return activeChildKey;
    }

    const branchIsActive =
      isPathActive(pathname, item.path) ||
      item.children.some((child) => hasActiveDescendant(child, pathname));

    if (branchIsActive) {
      return menuKey;
    }
  }

  return null;
};

const collectParentMenuKeys = (items: NavItem[], parentMenuKey = ''): string[] => {
  const keys: string[] = [];

  items.forEach((item) => {
    const menuKey = getMenuItemKey(item, parentMenuKey);
    if (item.children && item.children.length > 0) {
      keys.push(menuKey);
      keys.push(...collectParentMenuKeys(item.children, menuKey));
    }
  });

  return keys;
};

const NavItemComponent = ({
  item,
  closeMobileMenu,
  pathname,
  effectiveOpenMenuKey,
  onToggleMenu,
  onSelectLeaf,
  parentMenuKey = '',
  isChild = false,
}: {
  item: NavItem;
  closeMobileMenu: () => void;
  pathname: string;
  effectiveOpenMenuKey: string | null;
  onToggleMenu: (menuKey: string) => void;
  onSelectLeaf: (parentKey: string) => void;
  parentMenuKey?: string;
  isChild?: boolean;
}) => {
  const hasChildren = Boolean(item.children && item.children.length > 0);
  const menuKey = getMenuItemKey(item, parentMenuKey);
  const isActive = isPathActive(pathname, item.path);
  const isChildActive =
    hasChildren && item.children
      ? item.children.some((child) => hasActiveDescendant(child, pathname))
      : false;
  const isOpen = hasChildren && isMenuKeyInOpenBranch(menuKey, effectiveOpenMenuKey);
  const wasOpenRef = useRef(isOpen);
  const [shouldAnimateChildren, setShouldAnimateChildren] = useState(false);

  useEffect(() => {
    if (!wasOpenRef.current && isOpen) {
      setShouldAnimateChildren(true);
    }

    if (wasOpenRef.current && !isOpen) {
      setShouldAnimateChildren(false);
    }

    wasOpenRef.current = isOpen;
  }, [isOpen]);

  const handleClick = () => {
    if (hasChildren) {
      onToggleMenu(menuKey);
    } else if (item.path) {
      onSelectLeaf(parentMenuKey);
      closeMobileMenu();
    }
  };

  if (hasChildren) {
    return (
      <>
        <button
          onClick={handleClick}
          className={`
            flex w-full transform items-center justify-between rounded-lg px-3
            py-2 transition-all duration-200
            ease-in-out hover:translate-x-1
            ${
              isChildActive
                ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-dark-800'
            }
          `}
        >
          <div className="flex items-center space-x-3">
            {item.icon}
            <span className="font-medium">{item.name}</span>
          </div>
          <svg
            className={`h-4 w-4 transition-transform duration-300 ease-in-out ${isOpen ? 'rotate-180' : 'rotate-0'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <div
          className={`
            overflow-hidden transition-all duration-300 ease-in-out
            ${isOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}
          `}
        >
          {item.children && (
            <div className="relative ml-4 mt-1 space-y-1 border-l-2 border-gray-200 pl-2 dark:border-dark-700">
              {item.children.map((child, index) => (
                <div
                  key={child.path || child.name}
                  className={shouldAnimateChildren ? 'animate-slideIn' : ''}
                  style={
                    shouldAnimateChildren
                      ? {
                          animationDelay: `${index * 50}ms`,
                          animationFillMode: 'backwards',
                        }
                      : undefined
                  }
                >
                  <NavItemComponent
                    item={child}
                    closeMobileMenu={closeMobileMenu}
                    pathname={pathname}
                    effectiveOpenMenuKey={effectiveOpenMenuKey}
                    onToggleMenu={onToggleMenu}
                    onSelectLeaf={onSelectLeaf}
                    parentMenuKey={menuKey}
                    isChild={true}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </>
    );
  }
  if (!item.path) return null;

  return (
    <Link
      to={item.path}
      onClick={handleClick}
      className={`
        flex transform items-center space-x-3 rounded-lg px-3
        py-2 transition-all duration-200
        ease-in-out hover:translate-x-1 hover:shadow-sm
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-dark-900
        ${
          isChild
            ? isActive
              ? 'scale-100 bg-primary-100 text-sm text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
              : 'text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-dark-800/50 dark:hover:text-gray-200'
            : isActive
              ? 'bg-primary-50 text-primary-700 shadow-sm dark:bg-primary-900/20 dark:text-primary-300'
              : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-dark-800'
        }
      `}
    >
      <div className={`transition-transform duration-200 ${isActive ? 'scale-110' : 'scale-100'}`}>
        {item.icon}
      </div>
      <div className="flex min-w-0 items-center gap-2">
        <span className={isChild ? 'font-normal' : 'font-medium'}>{item.name}</span>
      </div>
    </Link>
  );
};

/**
 * Recursively filters menu items based on user permissions
 * Removes items that user doesn't have access to
 * If parent has no visible children after filtering, parent is also hidden
 */
const filterMenuByPermissions = (items: NavItem[], userRoles: UserRole[]): NavItem[] => {
  return items
    .map((item) => {
      // If item has children property (even if empty), process it
      if (item.children !== undefined) {
        const filteredChildren =
          item.children.length > 0 ? filterMenuByPermissions(item.children, userRoles) : [];

        // If no children remain after filtering
        if (filteredChildren.length === 0) {
          // Check if parent itself has permission to be shown
          // This allows dynamic menus (like Power BI Reports) to show even when initially empty
          if (item.roles) {
            const hasPermission = item.roles.some((role) => userRoles.includes(role));
            if (hasPermission) {
              return { ...item, children: [] }; // Keep parent visible for dynamic population
            }
            return null; // Hide if user doesn't have permission
          }
          // If no roles specified and has a path, keep it
          if (item.path) {
            return { ...item, children: [] };
          }
          return null; // Hide if no roles, no path, and no children
        }

        return { ...item, children: filteredChildren };
      }

      // For leaf items, check permissions
      if (item.roles && !item.roles.some((role) => userRoles.includes(role))) {
        return null; // Hide if user doesn't have required roles
      }

      return item;
    })
    .filter((item): item is NavItem => item !== null);
};

/**
 * Custom hook to get filtered menu items based on current user's permissions
 * Also injects dynamic Power BI reports into the Reports submenu
 */
const useFilteredMenu = () => {
  const userRoles = useUserRoles();
  const { reports } = usePowerBIStore();

  return useMemo(() => {
    // Create a shallow copy of navItems with Power BI reports injected
    let menuItems = [...navItems];

    // Find Market Information > Reports and inject Power BI reports
    if (reports.length > 0) {
      menuItems = navItems.map((item) => {
        if (item.name.toLowerCase() === 'market information' && item.children) {
          const updatedChildren = item.children.map((child) => {
            if (child.name.toLowerCase() === 'reports') {
              // Convert Power BI reports to NavItems - filter out invalid data
              const powerBINavItems: NavItem[] = reports
                .filter((report) => {
                  const isValid = report.id && report.name && report.type;
                  return isValid;
                })
                .map((report) => {
                  const endpoint = report.tokenEndpoint ? `/${report.tokenEndpoint}` : '';
                  return {
                    name: report.name,
                    path: `/reports/bi/${report.id}/${report.type}${endpoint}`,
                    icon: (
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    ),
                  };
                });

              return {
                ...child,
                children: powerBINavItems,
              };
            }
            return child;
          });

          return {
            ...item,
            children: updatedChildren,
          };
        }
        return item;
      });
    }

    const filteredMenu = filterMenuByPermissions(menuItems, userRoles);
    return filteredMenu;
  }, [userRoles, reports]);
};

export const Sidebar = () => {
  const location = useLocation();
  const { isMobileMenuOpen, closeMobileMenu, openMenuKey, setOpenMenuKey, clearOpenMenuKey } =
    useSidebarStore();
  const userRoles = useUserRoles();
  const { fetchAllReports } = usePowerBIStore();
  const { theme } = useThemeStore();
  const filteredNavItems = useFilteredMenu();
  const [showClearCacheModal, setShowClearCacheModal] = useState(false);

  const parentMenuKeys = useMemo(() => {
    return new Set(collectParentMenuKeys(filteredNavItems));
  }, [filteredNavItems]);

  const activeParentMenuKey = useMemo(() => {
    return findDeepestActiveParentKey(filteredNavItems, location.pathname);
  }, [filteredNavItems, location.pathname]);

  const effectiveOpenMenuKey = useMemo(() => {
    if (openMenuKey && parentMenuKeys.has(openMenuKey)) {
      return openMenuKey;
    }

    return activeParentMenuKey;
  }, [openMenuKey, parentMenuKeys, activeParentMenuKey]);

  // Fetch Power BI reports on mount if user has Power BI permissions
  useEffect(() => {
    const hasPowerBIAccess = userRoles.some((role) =>
      [
        UserRole.POWERBI_BASIC,
        UserRole.POWERBI_PRIVILEGED,
        UserRole.POWERBI_PRIVILEGED_KPC,
      ].includes(role)
    );

    if (hasPowerBIAccess) {
      fetchAllReports(userRoles);
    }
  }, [userRoles, fetchAllReports]);

  useEffect(() => {
    if (openMenuKey && !parentMenuKeys.has(openMenuKey)) {
      clearOpenMenuKey();
    }
  }, [openMenuKey, parentMenuKeys, clearOpenMenuKey]);

  const handleToggleMenu = (menuKey: string) => {
    if (!parentMenuKeys.has(menuKey)) {
      return;
    }

    if (openMenuKey === menuKey) {
      const separatorIndex = menuKey.lastIndexOf(MENU_KEY_SEPARATOR);
      if (separatorIndex !== -1) {
        setOpenMenuKey(menuKey.substring(0, separatorIndex));
      } else {
        clearOpenMenuKey();
      }
      return;
    }

    if (openMenuKey && isMenuKeyInOpenBranch(menuKey, openMenuKey)) {
      setOpenMenuKey(menuKey);
      return;
    }

    setOpenMenuKey(menuKey);
  };

  const handleSelectLeaf = (parentKey: string) => {
    if (!parentKey) {
      clearOpenMenuKey();
      return;
    }

    if (!parentMenuKeys.has(parentKey)) {
      clearOpenMenuKey();
      return;
    }

    setOpenMenuKey(parentKey);
  };

  const handleClearCache = () => {
    setShowClearCacheModal(true);
  };

  const confirmClearCache = async () => {
    try {
      // Get all localStorage keys first (to avoid iteration issues)
      const allKeys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          allKeys.push(key);
        }
      }

      // Helper function to check if key is authentication-related
      const isAuthKey = (key: string): boolean => {
        const lowerKey = key.toLowerCase();
        return (
          lowerKey.includes('msal') ||
          lowerKey.includes('login') ||
          lowerKey.includes('token') ||
          lowerKey.includes('account') ||
          lowerKey.includes('authority') ||
          lowerKey.includes('credential') ||
          lowerKey.includes('session') ||
          lowerKey.includes('idtoken') ||
          lowerKey.includes('accesstoken') ||
          lowerKey.includes('refreshtoken') ||
          // Azure AD / Microsoft identity keys often contain these patterns
          lowerKey.includes('microsoft') ||
          lowerKey.includes('azure') ||
          // Client ID patterns (UUIDs in keys)
          /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(key)
        );
      };

      // Remove only non-auth keys to preserve authentication
      allKeys.forEach((key) => {
        if (!isAuthKey(key)) {
          localStorage.removeItem(key);
        }
      });

      // Clear service worker cache if available
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
        }
      }

      // Clear cache storage (this is separate from MSAL which uses localStorage)
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
      }

      // Close the modal before reload
      setShowClearCacheModal(false);

      // Reload the page
      window.location.reload();
    } catch (error) {
      setShowClearCacheModal(false);
      window.location.reload();
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 top-16 z-20 bg-black bg-opacity-50 lg:hidden"
          onClick={closeMobileMenu}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          safe-bottom lg:safe-top fixed bottom-0 left-0
          top-16 z-30 w-[17rem] transform
          border-r border-gray-200 bg-white shadow-[6px_0_20px_-2px_rgba(0,0,0,0.15)]
          transition-transform duration-300 ease-in-out
          dark:border-dark-700 dark:bg-dark-900 dark:shadow-[6px_0_20px_-2px_rgba(0,0,0,0.4)] lg:top-0
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          flex flex-col
        `}
      >
        {/* Logo - hidden on mobile since Header shows it */}
        <div className="hidden h-20 items-center px-4 lg:flex">
          <Link to="/" onClick={closeMobileMenu} className="inline-flex items-center">
            <img
              src={`/assets/layout/images/logo-${theme === 'dark' ? 'dark' : 'light'}.png`}
              alt="Tankers International Logo"
              className="h-14 w-auto max-w-[220px] border-0 object-contain"
            />
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 overflow-y-auto px-4 py-6">
          {filteredNavItems.map((item) => (
            <NavItemComponent
              key={item.path || item.name}
              item={item}
              closeMobileMenu={closeMobileMenu}
              pathname={location.pathname}
              effectiveOpenMenuKey={effectiveOpenMenuKey}
              onToggleMenu={handleToggleMenu}
              onSelectLeaf={handleSelectLeaf}
            />
          ))}
        </nav>

        {/* Version Footer */}
        <div className="sticky bottom-0 w-full border-t border-gray-200 bg-white px-3 py-2 text-center text-sm dark:border-dark-700 dark:bg-dark-900">
          <button
            onClick={handleClearCache}
            className="cursor-pointer text-gray-500 opacity-50 transition-opacity hover:opacity-100 dark:text-gray-400"
            title="Click to clear cache and reset data"
          >
            {getVersionString()}
          </button>
        </div>
      </aside>

      {/* Clear Cache Confirmation Modal */}
      <Modal
        isOpen={showClearCacheModal}
        onClose={() => setShowClearCacheModal(false)}
        title="Clear Cache Confirmation"
        size="sm"
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setShowClearCacheModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" size="sm" onClick={confirmClearCache}>
              Clear Cache
            </Button>
          </>
        }
      >
        <div className="space-y-3 pb-4">
          <div className="flex items-start gap-3">
            <svg
              className="h-6 w-6 flex-shrink-0 text-yellow-600 dark:text-yellow-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Are you sure you want to reset all data? This will clear your local cache, remove all
              saved preferences, and reload the application.
            </p>
          </div>
        </div>
      </Modal>
    </>
  );
};
